/**
 * Vendor Bot Admin - Orders Page
 * Order management with filtering
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import OrderCard from '../../components/OrderCard';
import { useTelegram } from '../../hooks/useTelegram';

const FilterBar = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
  overflow-x: auto;
  padding-bottom: var(--spacing-sm);
`;

const FilterChip = styled.button<{ active: boolean }>`
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  white-space: nowrap;
  background: ${({ active }) => (active ? 'var(--accent-green)' : 'var(--bg-card)')};
  color: ${({ active }) => (active ? 'white' : 'var(--text-secondary)')};
  border: 1px solid ${({ active }) => (active ? 'var(--accent-green)' : 'var(--border-color)')};
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
`;

const Orders: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { showBackButton } = useTelegram();
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'cancelled'>('all');
  const [orders] = useState([
    {
      orderId: '12345',
      date: 'Oct 6, 2025',
      status: 'completed' as const,
      items: ['Premium Course', 'Bonus Material'],
      total: '$49.99',
    },
    {
      orderId: '12344',
      date: 'Oct 5, 2025',
      status: 'pending' as const,
      items: ['T-Shirt (Large)', 'Mug', 'Stickers'],
      total: '$45.50',
    },
    {
      orderId: '12343',
      date: 'Oct 4, 2025',
      status: 'processing' as const,
      items: ['Monthly Subscription'],
      total: '$9.99',
    },
    {
      orderId: '12342',
      date: 'Oct 3, 2025',
      status: 'cancelled' as const,
      items: ['Product A'],
      total: '$19.99',
    },
  ]);

  useEffect(() => {
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/dashboard`));
  }, [showBackButton, navigate, botUsername]);

  const filteredOrders = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

  return (
    <Layout header={<Navbar title="Orders" showBack />}>
      <FilterBar>
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          All Orders
        </FilterChip>
        <FilterChip active={filter === 'pending'} onClick={() => setFilter('pending')}>
          ⏳ Pending
        </FilterChip>
        <FilterChip active={filter === 'completed'} onClick={() => setFilter('completed')}>
          ✅ Completed
        </FilterChip>
        <FilterChip active={filter === 'cancelled'} onClick={() => setFilter('cancelled')}>
          ❌ Cancelled
        </FilterChip>
      </FilterBar>

      {filteredOrders.length === 0 ? (
        <EmptyState>No {filter !== 'all' ? filter : ''} orders found</EmptyState>
      ) : (
        <OrdersList>
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.orderId}
              {...order}
              onClick={() => navigate(`/vendor/${botUsername}/admin/order/${order.orderId}`)}
            />
          ))}
        </OrdersList>
      )}
    </Layout>
  );
};

export default Orders;
